
import React, { useState, useRef, useEffect } from 'react';

interface StoryBeat {
  id: string;
  name: string;
  description: string;
  prompt: string;
  example: string;
  x: number;
  y: number;
  placed: boolean;
}

interface DraggedItem {
  id: string;
  offsetX: number;
  offsetY: number;
}

const StoryArcBuilder: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [selectedBeat, setSelectedBeat] = useState<string | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [currentExample, setCurrentExample] = useState('harry-potter');
  
  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  const [storyBeats, setStoryBeats] = useState<StoryBeat[]>([
    {
      id: 'beginning',
      name: 'Beginning',
      description: 'Introduce your hero and their normal world',
      prompt: 'Who is your main character? What is their everyday life like before the adventure starts?',
      example: 'Harry lives with the Dursleys, not knowing he\'s a wizard',
      x: 100,
      y: 200,
      placed: false
    },
    {
      id: 'inciting',
      name: 'Call to Adventure',
      description: 'Something happens that starts the story',
      prompt: 'What event changes everything for your character? What problem do they discover?',
      example: 'Harry receives his Hogwarts letter and learns he\'s a wizard',
      x: 200,
      y: 160,
      placed: false
    },
    {
      id: 'rising',
      name: 'Rising Action',
      description: 'Your character faces challenges and learns',
      prompt: 'What obstacles does your character encounter? What do they learn along the way?',
      example: 'Harry learns about magic, makes friends, and discovers the mystery of the Philosopher\'s Stone',
      x: 300,
      y: 120,
      placed: false
    },
    {
      id: 'climax',
      name: 'Climax',
      description: 'The most exciting and important moment',
      prompt: 'What is the biggest challenge your character faces? This is the most exciting part!',
      example: 'Harry faces Quirrell and Voldemort to protect the Philosopher\'s Stone',
      x: 400,
      y: 80,
      placed: false
    },
    {
      id: 'falling',
      name: 'Falling Action',
      description: 'What happens right after the big moment',
      prompt: 'How does your character deal with what just happened? What are the immediate results?',
      example: 'Harry recovers in the hospital wing and learns what really happened',
      x: 500,
      y: 120,
      placed: false
    },
    {
      id: 'resolution',
      name: 'Resolution',
      description: 'How everything wraps up',
      prompt: 'How does your story end? How has your character changed?',
      example: 'Harry returns to the Dursleys, but now he knows he belongs in the wizarding world',
      x: 600,
      y: 160,
      placed: false
    }
  ]);

  const storyExamples = {
    'harry-potter': {
      title: 'Harry Potter and the Philosopher\'s Stone',
      beats: {
        beginning: 'Harry lives with the Dursleys, not knowing he\'s a wizard',
        inciting: 'Harry receives his Hogwarts letter and learns he\'s a wizard',
        rising: 'Harry learns about magic, makes friends, and discovers the mystery of the Stone',
        climax: 'Harry faces Quirrell and Voldemort to protect the Stone',
        falling: 'Harry recovers and learns what really happened',
        resolution: 'Harry returns to the Dursleys, but now he knows where he belongs'
      }
    },
    'toy-story': {
      title: 'Toy Story',
      beats: {
        beginning: 'Woody is Andy\'s favorite toy and leader of the toy room',
        inciting: 'Buzz Lightyear arrives and becomes Andy\'s new favorite',
        rising: 'Woody and Buzz get lost and must work together to get home',
        climax: 'They escape from Sid\'s house using teamwork',
        falling: 'Woody and Buzz make it back just as Andy is moving',
        resolution: 'Woody and Buzz become best friends and partners'
      }
    },
    'frozen': {
      title: 'Frozen',
      beats: {
        beginning: 'Elsa hides her ice powers and becomes queen',
        inciting: 'Elsa\'s powers are revealed and she flees to the mountains',
        rising: 'Anna goes to find Elsa, but Elsa accidentally hurts her',
        climax: 'Anna sacrifices herself to save Elsa from Hans',
        falling: 'Anna\'s sacrifice melts her frozen heart',
        resolution: 'Elsa learns to control her powers with love, not fear'
      }
    }
  };

  // SVG path points for the story mountain
  const mountainPath = "M 80 220 Q 200 160 320 120 Q 400 80 480 120 Q 600 160 720 200";
  const plotPoints = [
    { x: 100, y: 200 },
    { x: 200, y: 160 },
    { x: 320, y: 120 },
    { x: 400, y: 80 },
    { x: 500, y: 120 },
    { x: 600, y: 160 }
  ];

  const handleMouseDown = (e: React.MouseEvent, beatId: string) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    
    setDraggedItem({
      id: beatId,
      offsetX: e.clientX - rect.left - rect.width/2,
      offsetY: e.clientY - rect.top - rect.height/2
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggedItem || !svgRef.current) return;
    e.preventDefault();

    const svgRect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - svgRect.left - draggedItem.offsetX;
    const y = e.clientY - svgRect.top - draggedItem.offsetY;

    // Constrain to SVG bounds
    const constrainedX = Math.max(20, Math.min(780, x));
    const constrainedY = Math.max(20, Math.min(380, y));

    setStoryBeats(prev => prev.map(beat => 
      beat.id === draggedItem.id ? { ...beat, x: constrainedX, y: constrainedY } : beat
    ));
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!draggedItem || !svgRef.current) return;
    e.preventDefault();

    const svgRect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - svgRect.left;
    const y = e.clientY - svgRect.top;

    // Check if dropped near a plot point
    const nearestPointIndex = plotPoints.findIndex(point => {
      const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
      return distance < 40;
    });

    if (nearestPointIndex !== -1) {
      const nearestPoint = plotPoints[nearestPointIndex];
      // Check if this position is already occupied
      const occupied = storyBeats.find(beat => 
        beat.placed && beat.id !== draggedItem.id && 
        Math.abs(beat.x - nearestPoint.x) < 10 && Math.abs(beat.y - nearestPoint.y) < 10
      );
      
      if (!occupied) {
        setStoryBeats(prev => prev.map(beat => 
          beat.id === draggedItem.id 
            ? { ...beat, x: nearestPoint.x, y: nearestPoint.y, placed: true }
            : beat
        ));
      }
    } else {
      // If not dropped on a valid zone, return to original position or make unplaced
      setStoryBeats(prev => prev.map(beat => 
        beat.id === draggedItem.id 
          ? { ...beat, placed: false, x: 100 + Math.random() * 100, y: 300 + Math.random() * 50 }
          : beat
      ));
    }

    setDraggedItem(null);
  };

  // Add global mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!draggedItem || !svgRef.current) return;

      const svgRect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - svgRect.left - draggedItem.offsetX;
      const y = e.clientY - svgRect.top - draggedItem.offsetY;

      const constrainedX = Math.max(20, Math.min(780, x));
      const constrainedY = Math.max(20, Math.min(380, y));

      setStoryBeats(prev => prev.map(beat => 
        beat.id === draggedItem.id ? { ...beat, x: constrainedX, y: constrainedY } : beat
      ));
    };

    const handleGlobalMouseUp = () => {
      if (draggedItem) {
        setDraggedItem(null);
      }
    };

    if (draggedItem) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggedItem]);

  const resetArc = () => {
    setStoryBeats(prev => prev.map(beat => ({
      ...beat,
      placed: false,
      x: 50,
      y: 320 + Math.random() * 30
    })));
  };

  const autoPlace = () => {
    setStoryBeats(prev => prev.map((beat, index) => ({
      ...beat,
      x: plotPoints[index].x,
      y: plotPoints[index].y,
      placed: true
    })));
  };

  const completedBeats = storyBeats.filter(beat => beat.placed).length;
  const progressPercent = (completedBeats / storyBeats.length) * 100;

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary}, ${theme.primary})`,
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '20px',
        padding: '32px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: '0 0 12px 0'
          }}>
            Story Arc Builder
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: '#666',
            margin: '0 0 20px 0'
          }}>
            Drag the story beats onto the mountain to build your perfect plot!
          </p>
          
          {/* Progress */}
          <div style={{
            background: '#f0f0f0',
            borderRadius: '15px',
            height: '12px',
            margin: '0 auto 20px',
            maxWidth: '400px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: `linear-gradient(90deg, ${theme.success}, ${theme.blue})`,
              height: '100%',
              width: `${progressPercent}%`,
              borderRadius: '15px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          
          <div style={{
            fontSize: '1rem',
            color: theme.primary,
            fontWeight: '600'
          }}>
            {completedBeats} of {storyBeats.length} beats placed • {Math.round(progressPercent)}% complete
          </div>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '32px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setShowExamples(!showExamples)}
            style={{
              background: showExamples ? theme.blue : 'white',
              color: showExamples ? 'white' : theme.blue,
              border: `2px solid ${theme.blue}`,
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {showExamples ? 'Hide Examples' : 'Show Examples'}
          </button>
          
          <button
            onClick={autoPlace}
            style={{
              background: theme.success,
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            Auto-Place Beats
          </button>
          
          <button
            onClick={resetArc}
            style={{
              background: 'white',
              color: theme.primary,
              border: `2px solid ${theme.primary}`,
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            Reset
          </button>
        </div>

        {/* Examples Panel */}
        {showExamples && (
          <div style={{
            background: `${theme.blue}10`,
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px',
            border: `2px solid ${theme.blue}30`
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: theme.blue,
              margin: '0 0 16px 0'
            }}>
              Story Examples
            </h3>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              {Object.entries(storyExamples).map(([key, story]) => (
                <button
                  key={key}
                  onClick={() => setCurrentExample(key)}
                  style={{
                    background: currentExample === key ? theme.blue : 'white',
                    color: currentExample === key ? 'white' : theme.blue,
                    border: `2px solid ${theme.blue}`,
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  {story.title}
                </button>
              ))}
            </div>
            
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h4 style={{
                fontSize: '1.2rem',
                color: theme.blue,
                margin: '0 0 16px 0'
              }}>
                {storyExamples[currentExample as keyof typeof storyExamples].title}
              </h4>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {Object.entries(storyExamples[currentExample as keyof typeof storyExamples].beats).map(([beatKey, example]) => {
                  const beat = storyBeats.find(b => b.id === beatKey);
                  return (
                    <div
                      key={beatKey}
                      style={{
                        background: `${theme.blue}05`,
                        border: `1px solid ${theme.blue}20`,
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                    >
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: theme.blue,
                        marginBottom: '4px'
                      }}>
                        {beat?.name}
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#666',
                        lineHeight: '1.3'
                      }}>
                        {example}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main Interactive Area */}
        <div style={{
          display: 'flex',
          gap: '32px',
          alignItems: 'flex-start'
        }}>
          {/* Story Beats Palette */}
          <div style={{
            width: '280px',
            flexShrink: 0
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#333',
              margin: '0 0 16px 0'
            }}>
              Story Beats
            </h3>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              margin: '0 0 20px 0'
            }}>
              Drag these onto the story mountain in the right order!
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {storyBeats.map((beat) => (
                <div
                  key={beat.id}
                  onMouseDown={(e) => handleMouseDown(e, beat.id)}
                  onClick={() => setSelectedBeat(selectedBeat === beat.id ? null : beat.id)}
                  style={{
                    background: selectedBeat === beat.id ? `${theme.primary}15` : beat.placed ? '#f0f0f0' : 'white',
                    border: `2px solid ${selectedBeat === beat.id ? theme.primary : beat.placed ? '#ccc' : '#ddd'}`,
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: beat.placed ? 'default' : 'grab',
                    transition: 'all 0.2s ease',
                    userSelect: 'none',
                    opacity: beat.placed ? 0.5 : 1,
                    transform: draggedItem?.id === beat.id ? 'scale(1.05)' : 'scale(1)'
                  }}
                >
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: beat.placed ? '#999' : '#333',
                    marginBottom: '4px'
                  }}>
                    {beat.name} {beat.placed && '✓'}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: beat.placed ? '#999' : '#666',
                    lineHeight: '1.3'
                  }}>
                    {beat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Story Mountain SVG */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#333',
              margin: '0 0 16px 0'
            }}>
              Your Story Mountain
            </h3>
            
            <svg
              ref={svgRef}
              width="100%"
              height="400"
              viewBox="0 0 800 400"
              style={{
                background: `linear-gradient(135deg, ${theme.success}10, ${theme.blue}10)`,
                borderRadius: '16px',
                border: `2px solid ${theme.success}30`,
                cursor: draggedItem ? 'grabbing' : 'default'
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => setDraggedItem(null)}
            >
              {/* Story Mountain Path */}
              <path
                d={mountainPath}
                stroke={theme.success}
                strokeWidth="6"
                fill="none"
                strokeDasharray="10,5"
                opacity={0.7}
              />
              
              {/* Drop Zones */}
              {plotPoints.map((point, index) => (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r="25"
                  fill={`${theme.success}20`}
                  stroke={theme.success}
                  strokeWidth="3"
                  strokeDasharray="5,5"
                />
              ))}
              
              {/* Placed Story Beats */}
              {storyBeats.filter(beat => beat.placed).map((beat) => (
                <g key={beat.id}>
                  <circle
                    cx={beat.x}
                    cy={beat.y}
                    r="20"
                    fill={theme.primary}
                    stroke="white"
                    strokeWidth="3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedBeat(selectedBeat === beat.id ? null : beat.id)}
                  />
                  <text
                    x={beat.x}
                    y={beat.y + 5}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="bold"
                    fill="white"
                    style={{ pointerEvents: 'none' }}
                  >
                    {beat.name.split(' ')[0]}
                  </text>
                  <text
                    x={beat.x}
                    y={beat.y - 35}
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="bold"
                    fill="#333"
                    style={{ pointerEvents: 'none' }}
                  >
                    {beat.name}
                  </text>
                </g>
              ))}
              
              {/* Labels */}
              <text x="50" y="250" fontSize="16" fontWeight="bold" fill="#666">Start</text>
              <text x="720" y="250" fontSize="16" fontWeight="bold" fill="#666">End</text>
              <text x="400" y="50" fontSize="16" fontWeight="bold" fill="#666">Most Exciting!</text>
            </svg>
          </div>
        </div>

        {/* Selected Beat Details */}
        {selectedBeat && (
          <div style={{
            marginTop: '32px',
            background: `${theme.primary}10`,
            borderRadius: '16px',
            padding: '24px',
            border: `2px solid ${theme.primary}30`
          }}>
            {(() => {
              const beat = storyBeats.find(b => b.id === selectedBeat);
              if (!beat) return null;
              
              return (
                <>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: theme.primary,
                    margin: '0 0 12px 0'
                  }}>
                    {beat.name}
                  </h3>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#333',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5'
                  }}>
                    {beat.description}
                  </p>
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: theme.primary,
                      margin: '0 0 8px 0'
                    }}>
                      Writing Prompt:
                    </h4>
                    <p style={{
                      fontSize: '0.95rem',
                      color: '#333',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {beat.prompt}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Completion Message */}
        {completedBeats === storyBeats.length && (
          <div style={{
            marginTop: '32px',
            background: `linear-gradient(135deg, ${theme.success}, ${theme.blue})`,
            color: 'white',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              margin: '0 0 12px 0'
            }}>
              Congratulations! 🎉
            </h2>
            <p style={{
              fontSize: '1.2rem',
              margin: '0',
              opacity: 0.9
            }}>
              You've built a complete story arc! Now you're ready to write an amazing story.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryArcBuilder;